import * as ExcelJS from 'exceljs';

/**
 * Generate and download a price template Excel file
 */
export const generatePriceTemplate = async (): Promise<void> => {
  try {
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Price Template');
    
    // Define headers for price template
    const headers = [
      'SKU Code',
      'Description',
      'Style Code',
      'RRP (ex GST)',
      'BWA BUY PRICE 50% (ex GST)'
    ];
    
    // Add headers to the first row
    worksheet.addRow(headers);
    
    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
    
    // Set column widths
    worksheet.columns = [
      { width: 15 }, // SKU Code
      { width: 30 }, // Description
      { width: 15 }, // Style Code
      { width: 18 }, // RRP (ex GST)
      { width: 30 }  // BWA BUY PRICE 50% (ex GST)
    ];
    
    // Generate Excel file buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create blob and download
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'PriceTemplate.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating price template:', error);
    throw error;
  }
};

/**
 * Generate and download an inventory template Excel file
 */
export const generateInventoryTemplate = async (): Promise<void> => {
  try {
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Inventory Template');
    
    // Define headers for inventory template
    const headers = [
      'SKU Code',
      'Description', 
      'Style Code',
      'Carton',
      'Units per carton',
      'Carton Dimensions',
      'Weight per unit',
      'Weight per carton',
      'No. of units per pallet',
      'Pallet weight',
      'Available Stock'
    ];
    
    // Add headers to the first row
    worksheet.addRow(headers);
    
    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
    
    // Set column widths
    worksheet.columns = [
      { width: 15 }, // SKU Code
      { width: 25 }, // Description
      { width: 15 }, // Style Code
      { width: 10 }, // Carton
      { width: 15 }, // Units per carton
      { width: 20 }, // Carton Dimensions
      { width: 15 }, // Weight per unit
      { width: 15 }, // Weight per carton
      { width: 20 }, // No. of units per pallet
      { width: 15 }, // Pallet weight
      { width: 15 }  // Available Stock
    ];
    
    // Generate Excel file buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create blob and download
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'InventoryTemplate.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating inventory template:', error);
    throw error;
  }
};

/**
 * Generate and download a price list configuration template Excel file
 */
export const generatePriceListConfigurationTemplate = async (): Promise<void> => {
  try {
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Price Configuration Template');
    
    // Define headers for price configuration template
    // Based on the existing CSV template
    const headers = [
      'Regional Retail AUD Incl',
      'Regional Trade AUD Incl',
      'Metro Retail AUD Incl',
      'Metro Trade AUD Incl',
      'Regional Retail VIP AUD Incl',
      'Regional Trade VIP1 AUD Incl',
      'Regional Trade VIP2 AUD Incl',
      'Metro Retail VIP AUD Incl',
      'Metro Trade VIP1 AUD Incl',
      'Metro Trade VIP2 AUD Incl'
    ];
    
    // Add headers to the first row
    worksheet.addRow(headers);
    
    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
    
    // Set column widths
    worksheet.columns = headers.map(() => ({ width: 22 }));
    
    // Generate Excel file buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create blob and download
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'PriceListConfigurationTemplate.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating price list configuration template:', error);
    throw error;
  }
}; 

/**
 * Generate and download a customer history template Excel file
 */
export const generateCustomerHistoryTemplate = async (): Promise<void> => {
  try {
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Customer History Template');
    
    // Define headers for customer history template
    const headers = [
      'Type (Enter "Quote/Invoice")',
      'Invoice/Quote Number',
      'Store Type',
      'Created Date',
      'Grand Total (AUD)'
    ];
    
    // Add headers to the first row
    worksheet.addRow(headers);
    
    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
    
    // Set column widths
    worksheet.columns = [
      { width: 25 }, // Type
      { width: 20 }, // Invoice/Quote Number
      { width: 15 }, // Store Type
      { width: 15 }, // Created Date
      { width: 18 }  // Grand Total (AUD)
    ];
    
    // Add data validation for Type column (column A)
    // Apply validation to rows 2-1000 to allow for plenty of data
    for (let row = 2; row <= 1000; row++) {
      worksheet.getCell(`A${row}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"Invoice,Quote"'],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'Invalid Type',
        error: 'Please select either "Invoice" or "Quote".'
      };
    }
    
    // Add date format for Created Date column (column D)
    for (let row = 2; row <= 1000; row++) {
      worksheet.getCell(`D${row}`).numFmt = 'dd/mm/yyyy';
    }
    
    // Template is ready with headers only - no sample data
    
    // Generate Excel file buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create blob and download
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'CustomerHistoryTemplate.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating customer history template:', error);
    throw error;
  }
}; 